/**
 * Typed fetchers for /api/v1/validation/* — release validation runs.
 *
 * ⚠️ TEMPORARY LOCAL TYPES, for the same reason as `./ai.ts`: `pnpm gen:api`
 * needs the backend's dependency tree to dump `openapi.json`. The interfaces
 * mirror `backend/src/daquma/api/v1/validation.py` field-for-field; swap each
 * for `components["schemas"]["…"]` once the generator has been run.
 */

import { apiFetch } from "./apiFetch";

export type RunStatus =
  | "draft"
  | "assessed"
  | "risks_confirmed"
  | "planned"
  | "authored"
  | "executing"
  | "reported"
  | "approved"
  | "rejected";

export type TestStatus =
  | "draft"
  | "approved"
  | "assigned"
  | "in_progress"
  | "passed"
  | "failed"
  | "blocked";

export type TestMode = "manual" | "automatic";
export type Readiness = "go" | "go_with_conditions" | "no_go";

export interface ItqraScore {
  process_type: string | null;
  business_impact: string | null;
  use_frequency: string | null;
  process_rationale: string | null;
  impact_rationale: string | null;
  frequency_rationale: string | null;
}

export interface ItqraComputed {
  process_type: string;
  business_impact: string;
  use_frequency: string;
  weights: { process_type: number; business_impact: number; use_frequency: number };
  score: number;
  band: string;
  level: string;
}

export interface RiskItem {
  id: string;
  title: string;
  area: string | null;
  gxp_risk: string;
  category: string | null;
  /** Novo Nordisk's ITQRA buckets (from the model) and the platform-computed
   *  score/level. Null for a non-ITQRA assessment. */
  itqra: ItqraScore | null;
  itqra_computed: ItqraComputed | null;
  rationale: string | null;
  affected_objects: string[];
  source: string | null;
  confirmed: boolean;
  override_note: string | null;
  original_gxp_risk: string | null;
}

export interface TestStep {
  n: number;
  action: string;
  expected: string;
}

export interface ApiAssertion {
  description?: string | null;
  vql: string;
  expect: { kind: string; value?: number | string | null; field?: string | null };
}

export interface EvidenceOut {
  evidence_id: string;
  run_id: string;
  test_id: string;
  filename: string;
  content_type: string | null;
  size_bytes: number;
  sha256: string;
  note: string | null;
  uploaded_by_user_id: string | null;
  uploaded_by_display_name: string | null;
  created_at_utc: string;
}

export interface TestOut {
  test_id: string;
  run_id: string;
  seq: number;
  code: string;
  title: string;
  objective: string | null;
  risk_ref: string | null;
  requirement_ref: string | null;
  gxp_risk: string | null;
  preconditions: string | null;
  steps: TestStep[];
  test_data: { item: string; value?: string | null }[];
  reused_from: string | null;
  mode: TestMode;
  mode_rationale: string | null;
  api_assertions: ApiAssertion[];
  status: TestStatus;
  assignee_user_id: string | null;
  assignee_email: string | null;
  assignee_display_name: string | null;
  assigned_at_utc: string | null;
  executed_by_user_id: string | null;
  executed_by_display_name: string | null;
  executed_at_utc: string | null;
  step_results: Record<string, unknown>[] | null;
  observed_result: string | null;
  duration_ms: number | null;
  evidence: EvidenceOut[];
  created_at_utc: string;
  updated_at_utc: string;
}

export interface DefectOut {
  defect_id: string;
  run_id: string;
  test_id: string | null;
  code: string;
  title: string;
  severity: string;
  description: string | null;
  status: string;
  external_ref: string | null;
  created_at_utc: string;
}

export interface EvaluationRisk {
  id: string;
  title: string;
  gxp_risk: string;
  tests: string[];
  passed_tests: string[];
  failed_tests: string[];
  covered: boolean;
}

export interface Evaluation {
  tests: {
    total: number;
    passed: number;
    failed: number;
    blocked: number;
    open: number;
    open_codes: string[];
    manual: number;
    automatic: number;
  };
  risks: EvaluationRisk[];
  uncovered_high_medium: string[];
  defects: { code: string; severity: string; test_code: string; title: string }[];
  evidence_count_by_test: Record<string, number>;
  computed_readiness: Readiness;
  model_readiness: Readiness | null;
  readiness_mismatch: boolean;
  computed_at_utc: string;
}

export interface RunOut {
  run_id: string;
  title: string;
  vault_product: string;
  change_ref: string | null;
  description: string | null;
  status: RunStatus;
  stage_index: number;
  stages: string[];
  ai_connection_id: string;
  deployment: string;
  veeva_connection_id: string | null;
  blob_connection_id: string | null;
  risk_assessment_id: string | null;
  risks: RiskItem[] | null;
  risks_prompt_version: string | null;
  risks_inference_id: string | null;
  risks_confirmed_at_utc: string | null;
  plan_md: string | null;
  plan_version: number;
  plan_prompt_version: string | null;
  plan_inference_id: string | null;
  plan_saved_blob_path: string | null;
  tests_prompt_version: string | null;
  tests_inference_id: string | null;
  tests_approved_at_utc: string | null;
  evaluation: Evaluation | null;
  readiness: Readiness | null;
  report_md: string | null;
  report_prompt_version: string | null;
  report_inference_id: string | null;
  report_saved_blob_path: string | null;
  signature_id: string | null;
  approved_at_utc: string | null;
  approved_by_user_id: string | null;
  approved_by_display_name: string | null;
  rejection_reason: string | null;
  last_error: string | null;
  ado_work_item_id: number | null;
  ado_work_item_url: string | null;
  owner_user_id: string | null;
  owner_email: string | null;
  owner_display_name: string | null;
  is_owner: boolean;
  test_count: number;
  tests: TestOut[];
  defects: DefectOut[];
  created_at_utc: string;
  updated_at_utc: string;
}

export interface RunListOut {
  items: RunOut[];
}

export interface MyTestOut {
  test: TestOut;
  run_title: string;
  vault_product: string;
  change_ref: string | null;
  run_status: RunStatus;
}

export interface MyTestListOut {
  items: MyTestOut[];
}

export interface CreateRunIn {
  title: string;
  vault_product: string;
  change_ref?: string | null;
  description?: string | null;
  ai_connection_id: string;
  deployment: string;
  veeva_connection_id?: string | null;
  blob_connection_id?: string | null;
  risk_assessment_id?: string | null;
}

export interface UpdateTestIn {
  title?: string;
  objective?: string | null;
  preconditions?: string | null;
  risk_ref?: string | null;
  requirement_ref?: string | null;
  gxp_risk?: string | null;
  mode?: TestMode;
  mode_rationale?: string | null;
  steps?: TestStep[];
  test_data?: { item: string; value?: string | null }[];
  api_assertions?: ApiAssertion[];
  /** `""` unassigns. */
  assignee_user_id?: string;
}

export interface ManualResultIn {
  step_results: { n: number; result: "pass" | "fail" | "na"; note?: string | null }[];
  observed_result: string;
  verdict: "passed" | "failed" | "blocked";
}

export interface ExportIn {
  artefact: "plan" | "report";
  fmt: "md" | "pdf";
  container: string;
  path: string;
  blob_connection_id?: string | null;
}

const BASE = "/api/v1/validation";
const enc = encodeURIComponent;

export function listRuns(token: string): Promise<RunListOut> {
  return apiFetch<RunListOut>(`${BASE}/runs`, { token });
}

export function getRun(token: string, runId: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}`, { token });
}

export function createRun(token: string, input: CreateRunIn): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs`, { method: "POST", token, json: input });
}

export function deleteRun(token: string, runId: string, reason: string): Promise<void> {
  return apiFetch<void>(`${BASE}/runs/${enc(runId)}?reason=${enc(reason)}`, {
    method: "DELETE",
    token,
  });
}

export function attachAssessment(
  token: string,
  runId: string,
  riskAssessmentId: string,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/attach-assessment`, {
    method: "POST",
    token,
    json: { risk_assessment_id: riskAssessmentId },
  });
}

export function updateRisks(token: string, runId: string, items: RiskItem[]): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/risks`, {
    method: "PUT",
    token,
    json: { items },
  });
}

/** GATE 1 — freezes the risk items and drafts the plan in one call. */
export function confirmRisks(token: string, runId: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/confirm-risks`, {
    method: "POST",
    token,
  });
}

export function generatePlan(token: string, runId: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/generate-plan`, {
    method: "POST",
    token,
  });
}

export function updatePlan(
  token: string,
  runId: string,
  planMd: string,
  reason: string,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/plan`, {
    method: "PUT",
    token,
    json: { plan_md: planMd, reason },
  });
}

export function generateTests(
  token: string,
  runId: string,
  existingTestCases?: string | null,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/generate-tests`, {
    method: "POST",
    token,
    json: { existing_test_cases: existingTestCases ?? null },
  });
}

export function updateTest(
  token: string,
  runId: string,
  testId: string,
  input: UpdateTestIn,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/tests/${enc(testId)}`, {
    method: "PATCH",
    token,
    json: input,
  });
}

/** GATE 2 — every manual test has a tester; execution opens. */
export function approveTests(token: string, runId: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/approve-tests`, {
    method: "POST",
    token,
  });
}

export function executeAutomaticTest(
  token: string,
  runId: string,
  testId: string,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/tests/${enc(testId)}/execute`, {
    method: "POST",
    token,
  });
}

export function executeAllAutomatic(token: string, runId: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/execute-automatic`, {
    method: "POST",
    token,
  });
}

/** The run owner records a manual result on the tester's behalf. */
export function ownerManualResult(
  token: string,
  runId: string,
  testId: string,
  input: ManualResultIn,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/tests/${enc(testId)}/result`, {
    method: "POST",
    token,
    json: input,
  });
}

export function ownerAddEvidence(
  token: string,
  runId: string,
  testId: string,
  file: File,
  note?: string,
): Promise<RunOut> {
  const form = new FormData();
  form.append("file", file);
  if (note) form.append("note", note);
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/tests/${enc(testId)}/evidence`, {
    method: "POST",
    token,
    body: form,
  });
}

export function evaluateRun(token: string, runId: string, allowOpenTests = false): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/evaluate`, {
    method: "POST",
    token,
    json: { allow_open_tests: allowOpenTests },
  });
}

/** GATE 3 — electronic signature. The token must carry a fresh `auth_time`;
 *  a 401 `signature_reauth_required` means re-authenticate and retry. */
export function approveRun(token: string, runId: string, meaning: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/approve`, {
    method: "POST",
    token,
    json: { meaning },
  });
}

export function rejectRun(token: string, runId: string, reason: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/reject`, {
    method: "POST",
    token,
    json: { reason },
  });
}

export function reopenRun(token: string, runId: string, reason: string): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/reopen`, {
    method: "POST",
    token,
    json: { reason },
  });
}

export function exportArtefact(token: string, runId: string, input: ExportIn): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/export`, {
    method: "POST",
    token,
    json: input,
  });
}

/** Direct download URL (plan / report, md / pdf). Fetch with the bearer token
 *  and hand the blob to the browser — see `downloadArtefact`. */
export function artefactUrl(runId: string, artefact: "plan" | "report", fmt: "md" | "pdf"): string {
  return `${BASE}/runs/${enc(runId)}/artefact/${artefact}.${fmt}`;
}

export async function downloadArtefact(
  token: string,
  runId: string,
  artefact: "plan" | "report",
  fmt: "md" | "pdf",
): Promise<Blob> {
  const response = await fetch(artefactUrl(runId, artefact, fmt), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`Download failed (HTTP ${response.status})`);
  return response.blob();
}

export async function downloadEvidence(token: string, evidenceId: string): Promise<Blob> {
  const response = await fetch(`${BASE}/evidence/${enc(evidenceId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`Download failed (HTTP ${response.status})`);
  return response.blob();
}

export interface RunAssessmentInRunIn {
  blob_connection_id?: string | null;
  current: { container: string; path: string }[];
  upcoming: { container: string; path: string }[];
  title?: string | null;
}

/** Stage 1 without leaving the run: read the picked documents from Blob,
 *  produce the risk assessment, and extract its findings as risk items.
 *  Synchronous and slow (two model calls) — show progress. */
export function runAssessmentInRun(
  token: string,
  runId: string,
  input: RunAssessmentInRunIn,
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/run-assessment`, {
    method: "POST",
    token,
    json: input,
  });
}

// --- tester -------------------------------------------------------------

export function listMyTests(token: string): Promise<MyTestListOut> {
  return apiFetch<MyTestListOut>(`${BASE}/my-tests`, { token });
}

export function startMyTest(token: string, testId: string): Promise<MyTestOut> {
  return apiFetch<MyTestOut>(`${BASE}/my-tests/${enc(testId)}/start`, {
    method: "POST",
    token,
  });
}

export function addMyEvidence(
  token: string,
  testId: string,
  file: File,
  note?: string,
): Promise<MyTestOut> {
  const form = new FormData();
  form.append("file", file);
  if (note) form.append("note", note);
  return apiFetch<MyTestOut>(`${BASE}/my-tests/${enc(testId)}/evidence`, {
    method: "POST",
    token,
    body: form,
  });
}

export function submitMyResult(
  token: string,
  testId: string,
  input: ManualResultIn,
): Promise<MyTestOut> {
  return apiFetch<MyTestOut>(`${BASE}/my-tests/${enc(testId)}/result`, {
    method: "POST",
    token,
    json: input,
  });
}

/** Create an Azure DevOps work item from a defect and link it back. */
export function pushDefectToAdo(
  token: string,
  runId: string,
  defectId: string,
  connectionId: string,
  itemType = "Bug",
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/defects/${enc(defectId)}/push-to-ado`, {
    method: "POST",
    token,
    json: { connection_id: connectionId, item_type: itemType },
  });
}

/** Push the whole run to Azure DevOps as one work item, optionally parented
 *  under an RfC / requirement work item. */
export function pushRunToAdo(
  token: string,
  runId: string,
  connectionId: string,
  opts: { parentWorkItemId?: number | null; itemType?: string } = {},
): Promise<RunOut> {
  return apiFetch<RunOut>(`${BASE}/runs/${enc(runId)}/push-to-ado`, {
    method: "POST",
    token,
    json: {
      connection_id: connectionId,
      parent_work_item_id: opts.parentWorkItemId ?? null,
      item_type: opts.itemType ?? "Task",
    },
  });
}
